/**
 * ZoeChatWidget — floating bottom-right chat bubble
 * Small footprint, page stays fully scrollable/usable underneath.
 * Collapsed = avatar bubble. Expanded = 360x500 chat panel.
 */
import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, TextField, IconButton, CircularProgress, Avatar, Chip } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import VideocamIcon from "@mui/icons-material/Videocam";

const ZOE_AVATAR   = "/zoe-harlow.webp";
const CHAT_API     = "https://api.instacamgirls.com/api/zoe/chat";
const ROOM_API     = "https://api.instacamgirls.com/api/zoe/room";
const PRIVATE_URL  = "https://instacamgirlslive.com/?AFNO=1-94033";

// Trigger CTA after this many back-and-forth exchanges
const CTA_AFTER_MSGS = 4;

// Keywords that should trigger an immediate CTA
const HOT_KEYWORDS = [
  "naked", "nude", "tit", "tities", "boob", "boobs", "ass", "pussy", "cock",
  "private", "private show", "see you", "watch you", "cam", "live", "link",
  "page", "profile", "signup", "sign up", "join", "subscribe", "show me",
];

function shouldShowCta(messages, input) {
  const userMsgCount = messages.filter((m) => m.role === "user").length;
  if (userMsgCount >= CTA_AFTER_MSGS) return true;
  const lower = input.toLowerCase();
  return HOT_KEYWORDS.some((kw) => lower.includes(kw));
}

export default function ZoeChatModal({ onClose }) {
  const [open, setOpen]         = useState(true);
  const [messages, setMessages]   = useState([
    { role: "assistant", text: "Hey 😊 I'm Zoe. What's on your mind?" },
  ]);
  const [input, setInput]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [roomId, setRoomId]       = useState(null);
  const [showCta, setShowCta]     = useState(false);
  const bottomRef                 = useRef(null);

  useEffect(() => {
    fetch(ROOM_API, { method: "POST" })
      .then((r) => r.json())
      .then((d) => setRoomId(d.room_id))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    // Check CTA trigger before clearing input
    if (shouldShowCta(messages, text)) setShowCta(true);
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    setLoading(true);
    try {
      const res = await fetch(CHAT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: text,
          room_id: roomId,
          history: messages, // pass full conversation for context
        }),
      });
      const data = await res.json();
      setMessages((prev) => [...prev, { role: "assistant", text: data.content || "..." }]);
      if (data.room_id && !roomId) setRoomId(data.room_id);
      // Also trigger CTA after assistant's reply if threshold just reached
      setMessages((prev) => {
        if (prev.filter((m) => m.role === "user").length >= CTA_AFTER_MSGS) setShowCta(true);
        return prev;
      });
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", text: "Having a little trouble 😅 Try again?" }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <Box sx={{
      position: "fixed",
      bottom: { xs: 16, sm: 24 },
      right:  { xs: 16, sm: 24 },
      zIndex: 9000,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 1,
      pointerEvents: "none", // let clicks pass through to page by default
    }}>
      {/* Chat panel */}
      {open && (
        <Box sx={{
          width: { xs: 320, sm: 360 },
          height: 480,
          bgcolor: "#0d0d1a",
          border: "1px solid rgba(233,30,140,0.35)",
          borderRadius: 3,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 8px 40px rgba(0,0,0,0.7)",
          pointerEvents: "all",
        }}>
          {/* Header */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1.5,
            px: 2, py: 1.25,
            borderBottom: "1px solid rgba(255,255,255,0.07)",
            background: "linear-gradient(90deg,#1a0a2e,#0d0d1a)",
            flexShrink: 0,
          }}>
            <Box sx={{ position: "relative" }}>
              <Avatar src={ZOE_AVATAR} sx={{ width: 36, height: 36, border: "2px solid #e91e8c" }} />
              <Box sx={{ position:"absolute", bottom:0, right:0, width:9, height:9, borderRadius:"50%", bgcolor:"#4caf50", border:"2px solid #0d0d1a" }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display:"flex", alignItems:"center", gap:0.75 }}>
                <Typography sx={{ color:"#fff", fontWeight:700, fontSize:"0.9rem" }}>Zoe Harlow</Typography>
                <Chip
                  icon={<AutoAwesomeIcon sx={{ fontSize:"0.6rem !important", color:"#fff !important" }} />}
                  label="AI" size="small"
                  sx={{ bgcolor:"#e91e8c", color:"#fff", fontSize:"0.55rem", height:16, "& .MuiChip-icon":{color:"#fff"} }}
                />
              </Box>
              <Typography sx={{ color:"#4caf50", fontSize:"0.65rem" }}>Online now</Typography>
            </Box>
            <IconButton onClick={() => setOpen(false)} size="small" sx={{ color:"#666","&:hover":{color:"#fff"}, p:0.5 }}>
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={onClose} size="small" sx={{ color:"#666","&:hover":{color:"#fff"}, p:0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box sx={{ flex:1, overflowY:"auto", px:1.5, py:1.5, display:"flex", flexDirection:"column", gap:1.25,
            "&::-webkit-scrollbar":{ width:4 },
            "&::-webkit-scrollbar-track":{ bgcolor:"transparent" },
            "&::-webkit-scrollbar-thumb":{ bgcolor:"rgba(255,255,255,0.1)", borderRadius:2 },
          }}>
            {messages.map((msg, i) => (
              <Box key={i} sx={{ display:"flex", justifyContent: msg.role==="user"?"flex-end":"flex-start", alignItems:"flex-end", gap:0.75 }}>
                {msg.role === "assistant" && (
                  <Avatar src={ZOE_AVATAR} sx={{ width:26, height:26, flexShrink:0 }} />
                )}
                <Box sx={{
                  maxWidth:"78%", px:1.5, py:0.85,
                  borderRadius: msg.role==="user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                  bgcolor: msg.role==="user" ? "#e91e8c" : "rgba(255,255,255,0.08)",
                  color:"#fff", fontSize:"0.82rem", lineHeight:1.5,
                }}>
                  {msg.text}
                </Box>
              </Box>
            ))}
            {loading && (
              <Box sx={{ display:"flex", alignItems:"flex-end", gap:0.75 }}>
                <Avatar src={ZOE_AVATAR} sx={{ width:26, height:26 }} />
                <Box sx={{ px:1.5, py:0.85, borderRadius:"16px 16px 16px 4px", bgcolor:"rgba(255,255,255,0.08)" }}>
                  <CircularProgress size={12} sx={{ color:"#e91e8c" }} />
                </Box>
              </Box>
            )}
            {/* CTA card — fires after threshold or hot keywords */}
            {showCta && (
              <Box sx={{
                mx: 0.5, mt: 0.5, p: 1.5,
                borderRadius: 2.5,
                background: "linear-gradient(135deg, #1a0030 0%, #3d0060 100%)",
                border: "1px solid rgba(233,30,140,0.5)",
                display: "flex", flexDirection: "column", gap: 1,
                animation: "fadeIn 0.35s ease",
                "@keyframes fadeIn": { from: { opacity: 0, transform: "translateY(8px)" }, to: { opacity: 1, transform: "none" } },
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <VideocamIcon sx={{ color: "#e91e8c", fontSize: "1rem" }} />
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.82rem" }}>
                    Want to see the real thing? 🔥
                  </Typography>
                </Box>
                <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", lineHeight: 1.4 }}>
                  I'm live on cam right now. Join for free and come watch — private shows available.
                </Typography>
                <Box
                  component="a"
                  href={PRIVATE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75,
                    mt: 0.5, py: 1, borderRadius: 2,
                    background: "linear-gradient(90deg, #e91e8c, #c2185b)",
                    color: "#fff", fontWeight: 700, fontSize: "0.82rem",
                    textDecoration: "none",
                    boxShadow: "0 2px 12px rgba(233,30,140,0.45)",
                    transition: "opacity 0.15s",
                    "&:hover": { opacity: 0.88 },
                  }}
                >
                  <VideocamIcon sx={{ fontSize: "0.95rem" }} />
                  Watch Me Live — Free
                </Box>
              </Box>
            )}
            <div ref={bottomRef} />
          </Box>

          {/* Input */}
          <Box sx={{ px:1.25, py:1.25, borderTop:"1px solid rgba(255,255,255,0.07)", display:"flex", gap:0.75, flexShrink:0 }}>
            <TextField
              fullWidth size="small"
              placeholder="Message Zoe…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              multiline maxRows={2}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor:"rgba(255,255,255,0.05)", borderRadius:3,
                  color:"#fff", fontSize:"0.82rem",
                  "& fieldset":{ borderColor:"rgba(255,255,255,0.12)" },
                  "&:hover fieldset":{ borderColor:"rgba(233,30,140,0.4)" },
                  "&.Mui-focused fieldset":{ borderColor:"#e91e8c" },
                },
                "& .MuiInputBase-input::placeholder":{ color:"#555" },
              }}
            />
            <IconButton onClick={send} disabled={!input.trim() || loading}
              sx={{ bgcolor:"#e91e8c", color:"#fff", borderRadius:2, width:38, height:38, flexShrink:0,
                "&:hover":{ bgcolor:"#c2185b" },
                "&.Mui-disabled":{ bgcolor:"rgba(233,30,140,0.25)", color:"rgba(255,255,255,0.3)" },
              }}>
              <SendIcon sx={{ fontSize:"1rem" }} />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Collapsed bubble */}
      {!open && (
        <Box onClick={() => setOpen(true)} sx={{
          pointerEvents: "all",
          cursor: "pointer",
          position: "relative",
          width: 56, height: 56,
        }}>
          <Avatar src={ZOE_AVATAR} sx={{ width:56, height:56, border:"2.5px solid #e91e8c", boxShadow:"0 4px 20px rgba(233,30,140,0.5)" }} />
          <Box sx={{ position:"absolute", bottom:1, right:1, width:13, height:13, borderRadius:"50%", bgcolor:"#4caf50", border:"2px solid #0d0d1a" }} />
        </Box>
      )}
    </Box>
  );
}
